import React from 'react';
import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';
import { Link as ReactRouterLink } from 'react-router-dom';
import { colors, media, layout } from '@prisjakt/ui-base';
import ProductCheckbox from '@prisjakt/ui-product-checkbox';

// We want to exclude the hasExtra prop from forwarding, as it would otherwise be set on Link's DOM
// element, which triggers a React warning.
const Link = ({
  hasExtra,
  ...props
}: { hasExtra?: boolean } & React.ComponentProps<typeof ReactRouterLink>) => (
  <ReactRouterLink {...props} />
);

const SELECT_CHECKBOX_WIDTH = 30;

type TableRowProps = { selectable?: boolean };
export const TableRow = styled.div.attrs({ 'data-test': 'ProductListRow' })<
  TableRowProps
>`
  position: relative;
  display: table-row;
  border-bottom: 1px solid ${colors.lightGray1};
  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
  &:focus-within {
    background-color: rgba(0, 0, 0, 0.02);
  }
  ${media.mediumDown`
    display: flex;
    align-items: center;
  `};
  ${props =>
    props.selectable &&
    css`
      &:focus-within,
      &:hover {
        background-color: transparent;
      }
      &:hover label {
        box-shadow: 0 0 0 2px ${colors.yellow3};
      }
    `}
`;

export const LinkCell = styled(Link)`
  display: table-cell;
  vertical-align: middle;
`;

export const ImageWrapper = styled(LinkCell)`
  order: 0;
  width: 40px;
  text-align: center;
  height: auto;
  padding: 10px 5px;
`;

export const Image = styled.div`
  background: ${colors.lightGray1};
  border-radius: ${layout.borderRadius};
  width: 40px;
  height: 40px;

  img {
    display: block;
    max-width: 40px;
    max-height: 40px;
    width: auto;
    height: auto;
  }
`;

export const BasicInfo = styled(LinkCell)`
  flex: 1;
  min-width: 200px;
  position: relative;
  padding: 0px ${props => (props.hasExtra ? '70px' : '5px')} 0 5px;
  ${media.largeUp`
    max-width: 230px;
  `};
`;

export const ProductDetails = styled.div`
  display: inline-block;
`;

export const ProductName = styled.div.attrs({ 'data-test': 'ProductName' })`
  margin: 5px 0px 0px 0px;
`;

export const Subtitle = styled.div`
  color: ${colors.mediumGray1};
  font-size: 12px;
`;

export const BadgeArea = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  bottom: 10px;
  display: flex;
  align-items: center;
  flex-direction: column-reverse;
  justify-content: space-around;
  width: 25%;
`;

export const CampaignBadge = styled.span`
  display: block;
  padding: 5px;
  max-height: 55px;
  max-width: 55px;
`;

export const PriceLabelWrapper = styled(LinkCell)`
  outline: none;
  flex: 0 0 20%;
  text-align: right;
  order: 2;
  padding-right: 20px;
`;

export const StoreCount = styled.span`
  display: flex;
  justify-content: flex-end;
  font-size: 12px;
  color: ${p => p.theme.text.tertiary};
  fill: ${p => p.theme.icons.tertiary};
  align-items: center;

  svg {
    height: 16px !important;
    width: 16px !important;
    margin-left: 2px;
  }
`;

export const PropertyCell = styled(LinkCell)`
  outline: none;
  font-size: 12px;
  padding-right: 8px;
  color: ${p => p.theme.text.tertiary};
  width: ${switchProp(
    'width',
    {
      narrow: '160px',
      narrower: '120px',
      narrowest: '100px',
    },
    'auto'
  )};
  ${media.mediumDown`
    display: none;
  `};
`;

export const SelectBoxCell = styled.div`
  display: table-cell;
  width: ${SELECT_CHECKBOX_WIDTH}px;
`;

export const SelectBox = styled(ProductCheckbox)`
  box-sizing: border-box;
  margin: 10px 2px;
  width: ${SELECT_CHECKBOX_WIDTH}px;
  cursor: pointer;
`;
